import { ListItemText } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";

import TagField from "./TagField";
import { CONTENT_TYPE, EXPERIENCE_PRIMARY_FILTER } from "../../../../constants";
import { useUrlParams } from "../../../../hooks";
import { HumBurgurMenu } from "../../../../icons";
import { updateVisualBuilderData } from "../../../../store/actions";
import { visualBuilderDataSelector } from "../../../../store/selectors";
import { DraggableStyles } from "../../../containers/DraggableStyles";

const useStyles = makeStyles((theme) => ({
  hamburger: {
    fontSize: ".75rem !important",
  },
  hamburgerIconContainer: {
    height: "12px",
  },
  listItem: {
    color: "#fff",
    position: "relative",
    userSelect: "none",
    fontSize: "0.75rem",
    width: "100%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    border: 2,
    borderColor: "#FFF",
    padding: "0 15px 0 15px",
    "& svg": {
      marginRight: "0.5rem",
      color: "#FFF",
      visibility: "hidden",
      "& path": { stroke: "#FFF" },
    },
    "& form": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    "@media (min-width:767px)": {
      "&:hover": {
        "& svg": {
          visibility: (props) => (props.tabStatus === "customize" ? "inherit" : "hidden"),
        },
      },
    },
  },
  listChildItemRoot: {
    marginLeft: "8px",
    marginRight: 12,
  },
  listChildItemPrimary: {
    lineHeight: "1.5rem",
  },
  active: {
    color: theme.primaryColor,
    backgroundColor: "#000 !important",
  },
  contentListItem: {
    "&:last-child": {
      paddingBottom: 8,
    },
  },
}));

export default function TagItem(props) {
  const {
    filteredTagMediaItems,
    index,
    locked,
    onChangeContentSortOrder,
    onChangeTagStatus,
    onTagNameChangeComplete,
    providedDraggable = {},
    snapshotDraggable = {},
    startDrag,
    tabStatus,
    tag,
  } = props;

  const { id: tagId } = tag;

  const classes = useStyles(props);
  const dispatch = useDispatch();

  const params = useUrlParams();

  const { visualBuilderData } = useSelector(visualBuilderDataSelector({ ...params, tagId: tag.id }));

  const { primaryFilter, tagFilterId, gridView, tagsToggled = {}, currentMediaItemId } = visualBuilderData;

  const isToggleOff = tagFilterId === tagId;

  const onTagClick = () => {
    if (tag.hidden) {
      return;
    }

    const values = {
      tagFilterId: isToggleOff ? "" : tagId,
    };

    if (primaryFilter === EXPERIENCE_PRIMARY_FILTER.TAG && values.tagFilterId) {
      values.typeFilterId = null;
    }

    if (values.tagFilterId && filteredTagMediaItems[values.tagFilterId]) {
      values.currentMediaItemId = filteredTagMediaItems[values.tagFilterId][0].id;
    }

    if (!tagsToggled[tagId]) {
      values.tagsToggled = { ...tagsToggled, [tagId]: !tagsToggled[tagId] };
    }

    dispatch(updateVisualBuilderData(params, values, {}, true));
  };

  const onItemClick = (mediaItem) => {
    const { id: mediaItemId, xFrameOptions, contentType, link } = mediaItem;
    if (
      xFrameOptions &&
      xFrameOptions !== "NONE" &&
      contentType.value === CONTENT_TYPE.LINK &&
      window.confirm("Open link in new tab?")
    ) {
      window.open(link, "_blank");
    }

    const values = {};

    if (currentMediaItemId === mediaItemId && tagFilterId === tagId) {
      return;
    }

    if (tagFilterId !== tagId) {
      values.tagFilterId = tagId;
    }

    values.currentMediaItemId = mediaItemId;

    if (gridView) {
      values.gridView = false;
    }

    if (Object.keys(values).length) {
      dispatch(updateVisualBuilderData(params, values, {}, true));
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    if (
      result.destination.droppableId === result.source.droppableId &&
      result.destination.index === result.source.index
    ) {
      return;
    }

    onChangeContentSortOrder(result, tagId, index);
  };

  let listElementCssCls = classes.listItem;
  if (tagId === tagFilterId) {
    listElementCssCls = `${listElementCssCls} ${classes.active}`;
  }

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={onTagClick}
        className={listElementCssCls}
        style={{
          backgroundColor: snapshotDraggable.isDragging || tagFilterId === tagId ? "#000" : "inherit",
          width: "240px",
          paddingBottom: "5px",
        }}
        data-testid="tag-item-continer"
      >
        <div
          style={{ visibility: tag.hidden ? "hidden" : "initial" }}
          className={classes.hamburgerIconContainer}
          {...providedDraggable.dragHandleProps}
        >
          <HumBurgurMenu className={classes.hamburger} htmlColor="#d1d1d1" />
        </div>
        <TagField
          tag={tag}
          index={index}
          tabStatus={tabStatus}
          startDrag={startDrag}
          onTagNameChangeComplete={onTagNameChangeComplete}
          onChangeTagStatus={onChangeTagStatus}
          statusSelected={tagFilterId === tagId}
          toggleDropdown={tagsToggled[tagId]}
          toggleShowChildren={(e) => {
            e.stopPropagation();
            dispatch(
              updateVisualBuilderData(params, {
                tagsToggled: { ...tagsToggled, [tagId]: !tagsToggled[tagId] },
              }),
            );
          }}
          disabled={locked}
        />
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppableContentList" isDropDisabled={tabStatus !== "customize" || locked}>
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {!tag.hidden &&
                tagsToggled[tagId] &&
                filteredTagMediaItems[tagId]?.map((mediaItem, innerIndex) => {
                  const { id: mediaItemId } = mediaItem;
                  const active =
                    (currentMediaItemId === mediaItemId && tagFilterId === tagId) ||
                    (currentMediaItemId === mediaItemId && !tagFilterId);

                  return (
                    <Draggable
                      draggableId={`content-list-${mediaItemId}`}
                      index={innerIndex}
                      key={mediaItemId}
                      isDragDisabled={tag.hidden || tabStatus !== "customize"}
                      disableInteractiveElementBlocking={false}
                    >
                      {(innerProvidedDraggable, innerSnapshotDraggable) => {
                        const currentlyDragged =
                          innerProvidedDraggable &&
                          innerProvidedDraggable.dragHandleProps &&
                          innerProvidedDraggable.dragHandleProps["data-rbd-drag-handle-draggable-id"];

                        const getStyleResults = DraggableStyles(innerProvidedDraggable.draggableProps, false, () =>
                          onChangeTagStatus(tag, innerIndex),
                        );

                        const isCurrentlyDragged =
                          innerSnapshotDraggable.isDragging && currentlyDragged === `content-list-${mediaItemId}`;

                        return (
                          <div
                            className={classes.contentListItem}
                            ref={innerProvidedDraggable.innerRef}
                            {...innerProvidedDraggable.draggableProps}
                            style={{
                              ...getStyleResults,
                              zIndex: isCurrentlyDragged ? "3" : "1",
                            }}
                          >
                            <div
                              key={mediaItemId}
                              role="button"
                              tabIndex={0}
                              className={`${classes.listItem} ${classes.listChildItem} ${active && classes.active}`}
                              style={{
                                cursor: "pointer",
                                backgroundColor: innerSnapshotDraggable.isDragging || active ? "#000" : "inherit",
                                width: "240px",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onItemClick(mediaItem);
                              }}
                            >
                              <div
                                className={classes.hamburgerIconContainer}
                                {...innerProvidedDraggable.dragHandleProps}
                              >
                                <HumBurgurMenu className={classes.hamburger} htmlColor="#d1d1d1" />
                              </div>
                              <ListItemText
                                classes={{
                                  root: classes.listChildItemRoot,
                                  primary: classes.listChildItemPrimary,
                                }}
                                primary={`${mediaItem.name}` || "Untitled"}
                                style={{ fontStyle: mediaItem.name ? "" : "italic" }}
                              />
                            </div>
                          </div>
                        );
                      }}
                    </Draggable>
                  );
                })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
}
